import type { ThemeSettings } from './types'

const SYSTEM_FONTS = new Set([
  'arial',
  'helvetica',
  'georgia',
  'verdana',
  'tahoma',
  'times new roman',
  'trebuchet ms',
  'courier new',
  'system-ui',
  'sans-serif',
  'serif',
  'monospace',
])

const SIZES = {
  compact: { font: '14px', pad: '8px 10px', gap: '10px', padding: '16px' },
  normal: { font: '15px', pad: '11px 12px', gap: '14px', padding: '24px' },
  large: { font: '17px', pad: '14px 16px', gap: '18px', padding: '32px' },
} as const

function luminance(hex: string): number | null {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim())
  if (!match) return null
  const [r, g, b] = [0, 2, 4].map((start) => {
    const channel = Number.parseInt(match[1]!.slice(start, start + 2), 16) / 255
    return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4
  })
  return 0.2126 * r! + 0.7152 * g! + 0.0722 * b!
}

export function colorScheme(background: string): 'light' | 'dark' {
  const value = luminance(background)
  return value !== null && value < 0.4 ? 'dark' : 'light'
}

function safeCssValue(value: string): string {
  return value.replace(/[;{}<>\\]/g, '')
}

function fontStack(font: string | null): string {
  const base = 'system-ui,-apple-system,"Segoe UI",Roboto,sans-serif'
  return font ? `"${safeCssValue(font).replace(/"/g, '')}",${base}` : base
}

export function themeVariables(theme: ThemeSettings): string {
  const size = SIZES[theme.size] ?? SIZES.normal
  const pairs: [string, string][] = [
    ['primary', theme.primary],
    ['background', theme.background],
    ['text', theme.text],
    ['input-bg', theme.inputBackground],
    ['border', theme.border],
    ['error', theme.error],
    ['radius', `${Number(theme.radius) || 0}px`],
    ['font', fontStack(theme.font)],
    ['font-size', size.font],
    ['pad', size.pad],
    ['gap', size.gap],
    ['padding', size.padding],
  ]
  return pairs.map(([name, value]) => `--_${name}:var(--lf-${name},${safeCssValue(value)});`).join('')
}

export function buildCss(theme: ThemeSettings, scope: string): string {
  const align = { left: 'flex-start', center: 'center', right: 'flex-end' }[theme.buttonAlign] ?? 'center'
  const full = theme.buttonWidth === 'full'
  const focus =
    theme.inputStyle === 'underline'
      ? '.lf-input:focus-visible{border-bottom-color:var(--_primary);box-shadow:0 1px 0 0 var(--_primary)}'
      : '.lf-input:focus-visible{border-color:var(--_primary);box-shadow:0 0 0 3px color-mix(in srgb,var(--_primary) 25%,transparent)}'
  const input =
    theme.inputStyle === 'underline'
      ? 'border:0;border-bottom:1px solid var(--_border);border-radius:0;background-color:transparent;padding-inline:2px;'
      : theme.inputStyle === 'filled'
        ? 'border:1px solid transparent;background-color:color-mix(in srgb,var(--_text) 7%,var(--_input-bg));'
        : 'border:1px solid var(--_border);background-color:var(--_input-bg);'
  return `${scope}{${themeVariables(theme)}display:block;container-type:inline-size;box-sizing:border-box;color:var(--_text);font-family:var(--_font);font-size:var(--_font-size);line-height:1.4;background:var(--_background);border-radius:var(--_radius);padding:var(--_padding);color-scheme:${colorScheme(theme.background)}}
${scope} *,${scope} *::before,${scope} *::after{box-sizing:border-box}
.lf-form{display:grid;grid-template-columns:repeat(12,minmax(0,1fr));gap:var(--_gap);margin:0}
.lf-field{grid-column:span 12;display:flex;flex-direction:column;gap:6px;min-width:0}
.lf-label{font-weight:600;font-size:.93em}
.lf-req{color:var(--_error);margin-left:2px}
.lf-input{width:100%;margin:0;font:inherit;color:var(--_text);padding:var(--_pad);border-radius:var(--_radius);outline:none;min-height:2.6em;${input}}
${focus}
.lf-input[aria-invalid=true]{border-color:var(--_error)}
.lf-input:disabled{opacity:.6}
.lf-input::placeholder{color:color-mix(in srgb,var(--_text) 45%,transparent);opacity:1}
select.lf-input{appearance:none;-webkit-appearance:none;padding-right:2.4em;background-image:linear-gradient(45deg,transparent 50%,currentColor 50%),linear-gradient(135deg,currentColor 50%,transparent 50%);background-position:calc(100% - 1.15em) 55%,calc(100% - .8em) 55%;background-size:.36em .36em;background-repeat:no-repeat;cursor:pointer}
select.lf-input:has(option[value='']:checked){color:color-mix(in srgb,var(--_text) 45%,transparent)}
select.lf-input option{color:var(--_text);background:var(--_background)}
.lf-error{margin:0;color:var(--_error);font-size:.85em;min-height:0}
.lf-error:empty{display:none}
.lf-lgpd{grid-column:span 12;display:flex;flex-direction:column;gap:4px;font-size:.9em}
.lf-lgpd-row{display:flex;align-items:flex-start;gap:8px;flex-wrap:wrap}
.lf-lgpd-row input{appearance:none;-webkit-appearance:none;margin:.12em 0 0;width:1.15em;height:1.15em;flex:none;border:1.5px solid var(--_border);border-radius:min(var(--_radius),5px);background:var(--_input-bg);cursor:pointer;display:grid;place-content:center;transition:background-color .15s,border-color .15s}
.lf-lgpd-row input::after{content:"";width:.34em;height:.62em;border:solid #fff;border-width:0 2px 2px 0;transform:rotate(45deg) translate(-.04em,-.08em);opacity:0}
.lf-lgpd-row input:checked{background:var(--_primary);border-color:var(--_primary)}
.lf-lgpd-row input:checked::after{opacity:1}
.lf-lgpd-row input:focus-visible{outline:3px solid color-mix(in srgb,var(--_primary) 35%,transparent);outline-offset:2px}
.lf-lgpd-row input[aria-invalid=true]{border-color:var(--_error)}
.lf-link{background:none;border:0;padding:0;font:inherit;color:var(--_primary);text-decoration:underline;cursor:pointer}
.lf-actions{grid-column:span 12;display:flex;flex-direction:column;align-items:${align};gap:8px}
.lf-button{font:inherit;font-weight:600;cursor:pointer;border:0;border-radius:var(--_radius);padding:var(--_pad);padding-inline:1.6em;min-height:2.8em;background:var(--_primary);color:#fff;${full ? 'width:100%;' : ''}transition:filter .15s}
.lf-button:hover{filter:brightness(1.07)}
.lf-button:focus-visible{outline:3px solid color-mix(in srgb,var(--_primary) 40%,transparent);outline-offset:2px}
.lf-button[data-state=sending],.lf-button[data-state=redirecting]{cursor:progress;opacity:.85}
.lf-button[data-state=sent]{cursor:default}
.lf-form-error{margin:0;color:var(--_error);font-size:.9em;text-align:center}
.lf-form-error:empty{display:none}
.lf-hp{position:absolute!important;left:-9999px!important;width:1px;height:1px;overflow:hidden}
.lf-success{display:flex;flex-direction:column;justify-content:center;align-items:center;text-align:center;gap:6px;padding:24px 12px}
.lf-success-title{margin:0;font-size:1.2em;font-weight:700}
.lf-success-text{margin:0;opacity:.85}
.lf-skeleton{display:flex;flex-direction:column;justify-content:space-between;gap:var(--_gap)}
.lf-skeleton-row{display:flex;flex-direction:column;gap:6px}
.lf-skeleton-bar{border-radius:var(--_radius);background:color-mix(in srgb,var(--_text) 9%,transparent);animation:lf-pulse 1.4s ease-in-out infinite}
.lf-skeleton-bar::before{content:"\\a0";visibility:hidden}
.lf-skeleton-label{font-size:.93em;width:35%;border-radius:min(var(--_radius),6px)}
.lf-skeleton-input{padding:var(--_pad);min-height:2.6em}
.lf-skeleton-lgpd{font-size:.93em;width:70%;border-radius:min(var(--_radius),6px)}
.lf-skeleton-button{padding:var(--_pad);min-height:2.8em}
.lf-entra{animation:lf-entra .35s cubic-bezier(.2,.7,.2,1) both}
@keyframes lf-pulse{50%{opacity:.5}}
@keyframes lf-entra{from{opacity:0;transform:translateY(6px)}}
@media (prefers-reduced-motion:reduce){.lf-skeleton-bar{animation:none}.lf-button{transition:none}.lf-entra{animation:none}}
.lf-dialog{border:0;border-radius:var(--_radius);padding:0;max-width:min(560px,calc(100vw - 32px));color:var(--_text);background:var(--_background);font-family:var(--_font)}
.lf-dialog::backdrop{background:rgba(15,23,42,.5)}
.lf-dialog-body{padding:20px;max-height:70vh;overflow:auto}
.lf-dialog-body h2{margin:0 0 12px;font-size:1.1em}
.lf-dialog-body p{margin:0 0 10px;font-size:.93em}
.lf-dialog-actions{display:flex;justify-content:flex-end;gap:8px;padding:12px 20px;border-top:1px solid var(--_border)}
.lf-dialog-actions .lf-button{width:auto;min-height:2.4em}
.lf-secondary{background:transparent;color:var(--_text);border:1px solid var(--_border)}
.lf-message{margin:0;padding:12px;text-align:center;color:var(--_error)}
@container (max-width:480px){.lf-field{grid-column:span 12!important}}
`
}

export function isSystemFont(font: string): boolean {
  return SYSTEM_FONTS.has(font.trim().toLowerCase())
}

export function googleFontHref(font: string): string {
  const family = encodeURIComponent(font.trim()).replace(/%20/g, '+')
  return `https://fonts.googleapis.com/css2?family=${family}:wght@400;500;600;700&display=swap`
}

export function injectFont(font: string | null): void {
  if (!font || isSystemFont(font) || typeof document === 'undefined') return
  const head = document.head
  const href = googleFontHref(font)
  const existing = Array.from(head.querySelectorAll('link[data-lf-font]'))
  if (existing.some((link) => link.getAttribute('data-lf-font') === href)) return
  const hints: [string, boolean][] = [
    ['https://fonts.googleapis.com', false],
    ['https://fonts.gstatic.com', true],
  ]
  for (const [origin, crossOrigin] of hints) {
    if (head.querySelector(`link[rel="preconnect"][href="${origin}"]`)) continue
    const link = document.createElement('link')
    link.rel = 'preconnect'
    link.href = origin
    if (crossOrigin) link.crossOrigin = 'anonymous'
    head.appendChild(link)
  }
  const sheet = document.createElement('link')
  sheet.rel = 'stylesheet'
  sheet.href = href
  sheet.setAttribute('data-lf-font', href)
  head.appendChild(sheet)
}
